import { api } from './api';

// ==================== DTOs ====================

export interface PartnerProfileDto {
  id: string;
  userId: string;
  discipline: string;
  goals: string[];
  timezone: string;
  communicationStyle: string;
  checkInFrequency: string;
  experienceLevel: string;
  bio: string;
  isOptedIn: boolean;
  isAvailable: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface PartnershipDto {
  id: string;
  user1Id: string;
  user2Id: string;
  partnerUserId: string;
  status: string;
  matchScore: number;
  startedAt: string;
  endedAt?: string;
  endReason?: string;
  checkInStreak: number;
  lastCheckInAt?: string;
  partnerProfile?: PartnerProfileDto;
  createdAt: string;
}

export interface PartnerMessageDto {
  id: string;
  partnershipId: string;
  senderId: string;
  content: string;
  messageType: string;
  isRead: boolean;
  createdAt: string;
}

export interface MatchSuggestionDto {
  profile: PartnerProfileDto;
  matchScore: number;
  matchReason: string;
}

// ==================== Request Types ====================

export interface CreatePartnerProfileRequest {
  discipline: string;
  goals: string[];
  timezone: string;
  communicationStyle: string;
  checkInFrequency: string;
  experienceLevel: string;
  bio: string;
}

export interface UpdatePartnerProfileRequest {
  discipline: string;
  goals: string[];
  timezone: string;
  communicationStyle: string;
  checkInFrequency: string;
  experienceLevel: string;
  bio: string;
}

export interface SendPartnerMessageRequest {
  content: string;
  messageType?: string;
}

// ==================== Service ====================

export const partnersService = {
  // Profile
  async getProfile(): Promise<PartnerProfileDto | null> {
    try {
      return await api.get<PartnerProfileDto>('/partners/profile');
    } catch {
      return null;
    }
  },

  async createProfile(request: CreatePartnerProfileRequest): Promise<PartnerProfileDto> {
    return api.post<PartnerProfileDto>('/partners/profile', request);
  },

  async updateProfile(request: UpdatePartnerProfileRequest): Promise<PartnerProfileDto> {
    return api.put<PartnerProfileDto>('/partners/profile', request);
  },

  // Matching
  async getMatchSuggestions(): Promise<MatchSuggestionDto[]> {
    return api.get<MatchSuggestionDto[]>('/partners/matches');
  },

  async requestMatch(targetUserId: string): Promise<PartnershipDto> {
    return api.post<PartnershipDto>(`/partners/matches/${targetUserId}`, {});
  },

  async acceptMatch(id: string): Promise<PartnershipDto> {
    return api.post<PartnershipDto>(`/partners/matches/${id}/accept`, {});
  },

  async declineMatch(id: string): Promise<void> {
    await api.post(`/partners/matches/${id}/decline`, {});
  },

  // Partnership
  async getActivePartnership(): Promise<PartnershipDto | null> {
    try {
      return await api.get<PartnershipDto>('/partners/partnership');
    } catch {
      return null;
    }
  },

  async getPartnershipHistory(): Promise<PartnershipDto[]> {
    return api.get<PartnershipDto[]>('/partners/partnership/history');
  },

  async endPartnership(reason?: string): Promise<void> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    await api.delete(`/partners/partnership${params}`);
  },

  // Messages
  async getMessages(pageNumber = 1, pageSize = 50): Promise<PartnerMessageDto[]> {
    return api.get<PartnerMessageDto[]>(`/partners/messages?pageNumber=${pageNumber}&pageSize=${pageSize}`);
  },

  async sendMessage(request: SendPartnerMessageRequest): Promise<PartnerMessageDto> {
    return api.post<PartnerMessageDto>('/partners/messages', request);
  },
};
